/*
  # Fixed timezone calculations for Costa Rica
  
  1. Changes
    - Fixed Costa Rica timezone calculation using proper UTC offset
    - Removed problematic toLocaleString() conversions
    - Added helper function to get current Costa Rica time
    - Updated booking validation to use consistent timezone logic
*/

const COSTA_RICA_UTC_OFFSET = -6; // Costa Rica is UTC-6
const MIN_HOURS_BEFORE_DEPARTURE = 12;

interface TimeFormat {
  americanTime: string;
  europeanTime: string;
}

// Helper function to get current time in Costa Rica
export const getCostaRicaTime = (): Date => {
  const now = new Date();
  // Costa Rica is UTC-6, so subtract 6 hours from UTC
  return new Date(now.getTime() + (COSTA_RICA_UTC_OFFSET * 60 * 60 * 1000));
};

// Helper function to create a date in Costa Rica timezone
export const createCostaRicaDate = (year: number, month: number, day: number, hours = 0, minutes = 0): Date => {
  // Create date in UTC, then adjust for Costa Rica offset
  const utcDate = new Date(Date.UTC(year, month, day, hours, minutes));
  return new Date(utcDate.getTime() - (COSTA_RICA_UTC_OFFSET * 60 * 60 * 1000));
};

export const formatDate = (date: Date, options: Intl.DateTimeFormatOptions = {}): string => {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Costa_Rica',
    ...options
  }).format(date);
};

export const formatTime = (time: string): TimeFormat => {
  const [hours, minutes] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  
  return {
    americanTime: `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`,
    europeanTime: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  };
};

export const isValidBookingTime = (tourDate: Date, departureTime: string): boolean => {
  const [hours, minutes] = departureTime.split(':').map(Number);
  const tourDateTime = new Date(tourDate);
  tourDateTime.setHours(hours, minutes, 0, 0);

  // Get current time in Costa Rica
  const costaRicaNow = getCostaRicaTime();
  
  // Check if the tour date is within the next 12 months
  const maxBookingDate = new Date(costaRicaNow);
  maxBookingDate.setFullYear(maxBookingDate.getFullYear() + 1);
  
  if (tourDateTime > maxBookingDate) {
    return false;
  }

  // Calculate hours until departure
  const hoursUntilDeparture = (tourDateTime.getTime() - costaRicaNow.getTime()) / (1000 * 60 * 60);
  
  return hoursUntilDeparture >= MIN_HOURS_BEFORE_DEPARTURE;
};